/**
 * Factory authoring agent — load session and send messages (tool-using agent).
 * Blueprint tool writes sync to the form via returned patches.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  getAgentAuthoringSession,
  sendAgentAuthoringMessage,
} from '@/services/aiStudioAgentService';
import type {
  AuthoringAgentPatch,
  AuthoringMessage,
  AuthoringVersionPatch,
  DefinitionProgress,
  StudioAgentArchetype,
  StudioComputeClass,
  StudioExecutionMode,
} from '@/types/aiStudioAgent';
import type { ConversationMessage } from '@/types/aiStudio';
import { stripAssistantDisplayContent } from '@/components/ai-studio/conversation/stripAssistantDisplayContent';
import {
  applyAuthoringPatches,
  hasAgentPatchContent,
  hasVersionPatchContent,
} from './applyAuthoringPatches';

function toShellMessages(rows: AuthoringMessage[]): ConversationMessage[] {
  return rows.map((row) => ({
    id: row.id,
    role: row.role,
    content:
      row.role === 'assistant' ? stripAssistantDisplayContent(row.content) : row.content,
    createdAt: row.createdAt,
  }));
}

export interface AgentAuthoringApplyHandlers {
  onApplyAgentPatch?: (patch: AuthoringAgentPatch) => void;
  onApplyVersionPatch?: (patch: {
    contextDefinition?: string;
    successCriteria?: string;
    explainInstructions?: string;
    executionMode?: StudioExecutionMode;
    computeClasses?: StudioComputeClass[];
    archetype?: StudioAgentArchetype | null;
    inputExpected?: AuthoringVersionPatch['inputExpected'];
    outputExpected?: AuthoringVersionPatch['outputExpected'];
  }) => void;
}

export interface UseAgentAuthoringResult {
  messages: ConversationMessage[];
  progress: DefinitionProgress | null;
  loading: boolean;
  sending: boolean;
  errorKey: string | null;
  send: (text: string) => Promise<void>;
}

export function useAgentAuthoring(
  agentId: string | undefined,
  versionId: string | undefined,
  enabled: boolean,
  applyHandlers?: AgentAuthoringApplyHandlers
): UseAgentAuthoringResult {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [progress, setProgress] = useState<DefinitionProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !agentId) return;
    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    getAgentAuthoringSession(agentId, versionId)
      .then((session) => {
        if (cancelled) return;
        setMessages(toShellMessages(session.messages));
        setProgress(session.progress);
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.authoring.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, versionId, enabled]);

  const send = useCallback(
    async (text: string) => {
      if (!agentId || !text.trim()) return;
      setSending(true);
      setErrorKey(null);
      const optimisticId = `pending-user-${Date.now()}`;
      setMessages((current) => [
        ...current,
        {
          id: optimisticId,
          role: 'user',
          content: text.trim(),
          createdAt: new Date().toISOString(),
        },
        {
          id: `pending-assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
          createdAt: new Date().toISOString(),
          pending: true,
        },
      ]);
      try {
        const result = await sendAgentAuthoringMessage(agentId, text.trim(), versionId);
        const agentPatch = result.assistantMessage.suggestedAgentPatch;
        const versionPatch = result.assistantMessage.suggestedVersionPatch;
        const displayContent = stripAssistantDisplayContent(result.assistantMessage.content);

        if (applyHandlers) {
          applyAuthoringPatches(agentPatch, versionPatch, applyHandlers);
        }

        setMessages((current) => {
          const withoutPending = current.filter((m) => !m.pending && m.id !== optimisticId);
          return [
            ...withoutPending,
            {
              id: result.userMessage.id,
              role: 'user',
              content: result.userMessage.content,
              createdAt: result.userMessage.createdAt,
            },
            {
              id: result.assistantMessage.id,
              role: 'assistant',
              content: displayContent,
              createdAt: result.assistantMessage.createdAt,
            },
          ];
        });
        setProgress(result.progress);
      } catch {
        setErrorKey('aiStudio.authoring.sendFailed');
        setMessages((current) => current.filter((m) => !m.pending && m.id !== optimisticId));
      } finally {
        setSending(false);
      }
    },
    [agentId, versionId, applyHandlers]
  );

  return {
    messages,
    progress,
    loading,
    sending,
    errorKey,
    send,
  };
}

/** @deprecated Patches auto-apply; kept for tests importing patch detection. */
export { hasAgentPatchContent, hasVersionPatchContent };
